import React from 'react';
import { useParams, Navigate, Link as RouterLink } from 'react-router-dom';
import { Box, Button, Link, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { BRAND_SANS_FONT } from '../../shared/design/theme';
import { Container, LandingContainer } from '../home/HomePage.styles';
import { ArrowBackIcon } from '../../shared/icons';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import { Footer } from '../../shared/components/Footer';
import { SiteHeader } from '../../shared/components/SiteHeader';
import BlogArticleBody from './BlogArticleBody';
import SEO from '../../shared/components/SEO';
import '../home/HomePage.styles.css';

const ACCENT = '#B83B1D';

// Shared button states. Focus-visible and active were missing on the one
// control this page had.
const btnStates = {
  transition: 'background 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease, transform 0.12s ease',
  '&:active': { transform: 'translateY(1px)' },
  '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: '3px' },
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
};

// ---------------------------------------------------------------------------
// Blog post data
// ---------------------------------------------------------------------------

// `image` is the social/share card for the post. It is explicit rather than
// derived from the slug: the old `/blog/<slug>.jpg` guess resolved to a file
// that does not exist for pe-software-led, so that post shared with a broken
// preview image.
const blogPosts = {
  'brand-guidelines': {
    category: 'Product',
    title: 'Why Your AI Slide Tool Ignores Your Brand Guidelines (And How to Fix It)',
    readTime: '4 min read',
    date: 'March 2026',
    image: '/blog/brand-guidelines.jpg',
    content: [
      {
        type: 'paragraph',
        text: 'Every consulting firm, investment bank, and PE shop has a slide master. It encodes years of brand decisions: the exact shade of navy, the approved font stack, the margin widths, the footer format. It is the visual language of the firm.',
      },
      {
        type: 'paragraph',
        text: 'And yet, every AI presentation tool on the market ignores it completely.',
      },
      {
        type: 'heading',
        text: 'The problem: AI slides built in a vacuum',
      },
      {
        type: 'paragraph',
        text: 'Most AI slide tools work by generating content in their own environment and exporting a finished file. They have no awareness of your existing templates, your slide master, or your brand guidelines. The result is a deck that looks like it was made by someone who has never seen your firm\'s work before - because, functionally, it was.',
      },
      {
        type: 'paragraph',
        text: 'The typical workflow looks like this: generate slides in the AI tool, export to PowerPoint, then spend 2-3 hours reformatting every slide to match your templates. You\'re fixing fonts, adjusting colors, repositioning elements, resizing charts, and rebuilding layouts that the AI tool built from scratch instead of building on top of your existing framework.',
      },
      {
        type: 'paragraph',
        text: 'For an associate billing $200/hour, that reformatting time isn\'t free. And when the deliverable is due at 6am, those hours matter.',
      },
      {
        type: 'heading',
        text: 'Why this happens',
      },
      {
        type: 'paragraph',
        text: 'The root cause is architectural. Most AI presentation tools are standalone web applications. They generate slides in their own rendering engine, using their own default templates. They literally cannot read your slide master because they don\'t operate inside PowerPoint.',
      },
      {
        type: 'paragraph',
        text: 'Your slide master is a set of XML definitions embedded in the .pptx file. It defines layouts, color schemes, font themes, placeholder positions, and default formatting. To respect it, a tool needs to read those definitions and build content on top of them - not alongside them.',
      },
      {
        type: 'heading',
        text: 'The fix: work inside PowerPoint, not outside it',
      },
      {
        type: 'paragraph',
        text: 'The solution is straightforward in principle: the AI needs to operate inside the user\'s PowerPoint environment, reading the active slide master and building slides that inherit from existing layouts. When the AI creates a new slide, it should use your firm\'s title slide layout. When it sets a font, it should pull from your theme\'s font scheme. When it picks a color, it should reference your palette.',
      },
      {
        type: 'paragraph',
        text: 'This is how Restack works. It runs as a PowerPoint add-in - inside the application, not as a separate tool. When it reads your deck, it reads the slide master. When it creates content, it creates content on your templates. The output doesn\'t need reformatting because it was never off-brand to begin with.',
      },
      {
        type: 'heading',
        text: 'What to look for',
      },
      {
        type: 'paragraph',
        text: 'If you\'re evaluating AI tools for slide work, ask one question: does it read my slide master? If the answer is no - if the tool generates slides in its own environment and exports a file - you\'re signing up for a reformatting step that defeats most of the time savings.',
      },
      {
        type: 'paragraph',
        text: 'The promise of AI for presentations isn\'t just faster content generation. It\'s faster content generation that\'s actually usable. And "usable" means on-brand, on-template, and ready to send.',
      },
    ],
  },
  'cost-of-powerpoint': {
    category: 'Industry',
    title: 'The Real Cost of PowerPoint in Professional Services',
    readTime: '6 min read',
    date: 'March 2026',
    image: '/blog/cost-of-powerpoint.jpg',
    content: [
      {
        type: 'paragraph',
        text: 'Here\'s a number that should bother every managing director in professional services: associates at top consulting firms and investment banks spend between 20 and 40 hours per week in PowerPoint. Not researching. Not analyzing. Not thinking. Formatting.',
      },
      {
        type: 'paragraph',
        text: 'At a fully loaded cost of $150,000-$200,000 per year for a junior associate, that\'s $75 to $100 per hour spent on slide alignment, font consistency, and chart formatting. Multiply that across a team of 8-10 associates and the annual cost of PowerPoint busywork reaches seven figures.',
      },
      {
        type: 'heading',
        text: 'Where the time actually goes',
      },
      {
        type: 'paragraph',
        text: 'It\'s not the content creation that takes time. A good associate can outline a 30-slide CIM or strategy deck in an hour. The bottleneck is production: building each slide from the template, formatting tables, creating charts, ensuring visual consistency, and then iterating through 3-5 rounds of revisions where the feedback is overwhelmingly about formatting rather than substance.',
      },
      {
        type: 'paragraph',
        text: '"Move this chart 2px to the right." "The font on slide 14 doesn\'t match slide 7." "The source citation format changed - update all 40 slides." These are real comments from real review cycles, and each one triggers a manual edit-export-review loop.',
      },
      {
        type: 'heading',
        text: 'The hidden costs',
      },
      {
        type: 'paragraph',
        text: 'The direct labor cost is only part of the picture. There are at least three hidden costs that firms rarely quantify:',
      },
      {
        type: 'paragraph',
        text: 'First, opportunity cost. Every hour an associate spends on formatting is an hour they\'re not spending on analysis, client relationships, or professional development. Senior partners regularly complain that junior staff "can\'t think strategically" - but how can they, when 60% of their time is spent as a human rendering engine?',
      },
      {
        type: 'paragraph',
        text: 'Second, turnover. The number one reason junior professionals cite for leaving consulting and banking isn\'t the pay or the hours - it\'s the nature of the work. "I didn\'t get an MBA to fix font sizes" is a sentiment so common it\'s become a meme. Replacing an associate costs 1.5-2x their annual salary.',
      },
      {
        type: 'paragraph',
        text: 'Third, quality degradation under time pressure. When a deck is due at 6am and the associate has been formatting since midnight, mistakes happen. Misaligned charts. Stale data in a table. A slide from a previous deal that didn\'t get updated. These errors have real consequences - they erode client trust and create legal risk.',
      },
      {
        type: 'heading',
        text: 'What the math actually says',
      },
      {
        type: 'paragraph',
        text: 'Let\'s be specific. A mid-market PE fund with a deal team of 6 (2 VPs, 4 associates) produces roughly 15-20 major deliverables per quarter: CIMs, management presentations, board decks, and investment committee materials. Each deliverable requires 30-60 hours of slide production.',
      },
      {
        type: 'paragraph',
        text: 'At the low end, that\'s 450 hours per quarter of slide production - approximately $45,000 in direct labor cost. At the high end, it\'s 1,200 hours and $120,000. Annually, a single deal team spends $180,000-$480,000 on PowerPoint production.',
      },
      {
        type: 'paragraph',
        text: 'If AI tooling can reduce that production time by 50-70% - which is what early adopters are reporting - the annual savings per team are $90,000-$340,000. That\'s before accounting for reduced turnover, fewer errors, and faster deal cycles.',
      },
      {
        type: 'heading',
        text: 'The bottom line',
      },
      {
        type: 'paragraph',
        text: 'PowerPoint is the most expensive software in professional services. Not because of the license cost - because of the labor cost. And for the first time, there are tools that can meaningfully reduce that labor without compromising the quality bar that clients expect.',
      },
      {
        type: 'paragraph',
        text: 'The firms that figure this out first will have a structural advantage: faster deliverables, lower costs, and happier teams. The firms that don\'t will keep paying $100/hour for font alignment.',
      },
    ],
  },
  'pe-software-led': {
    category: 'Industry',
    title: 'Half the PE Industry Calls Itself Software-Led Now',
    readTime: '4 min read',
    date: 'May 2026',
    image: '/blog/k-shaped-future-of-software.png',
    content: [
      {
        type: 'image',
        src: '/blog/k-shaped-future-of-software.png',
        alt: 'The K-shaped future of software: winners pull away while the rest fall below the market-growth line.',
      },
      {
        type: 'paragraph',
        text: 'Half the PE industry calls itself software-led now. It sounds excellent in an LP meeting. The decks are immaculate. Everyone has read the Bain report. Someone has said "agentic" with a straight face.',
      },
      {
        type: 'paragraph',
        text: 'Then you ask what the AI plan actually looks like at portfolio level. Usually, it is a job title.',
      },
      {
        type: 'paragraph',
        text: 'A Head of AI hire. An offsite with OpenAI. A few pilots. A steering committee. Then a press release written in that strange hostage-note language where nobody is allowed to say what the business actually does.',
      },
      {
        type: 'paragraph',
        text: 'Meanwhile, nothing important has changed. The product is still the same product. The pricing still assumes old switching costs. The roadmap still has the same 14 epics it had two years ago. The customer is already testing three vendors, one internal tool and something built by a 24-year-old in procurement who is now accidentally more strategic than the board.',
      },
      {
        type: 'heading',
        text: 'The stickiness was mostly friction',
      },
      {
        type: 'paragraph',
        text: 'PE bought software because it was sticky. But a lot of the stickiness was never product love. It was friction. Bad integrations. Workflow lock-in. Training pain. Data trapped in weird corners. Contract fatigue. The glorious moat of "we cannot be bothered to move."',
      },
      {
        type: 'paragraph',
        text: 'AI eats that first.',
      },
      {
        type: 'heading',
        text: 'K-shaped, not a rising tide',
      },
      {
        type: 'paragraph',
        text: 'The Bain report calls it K-shaped. CNBC said PE may end up eating its own software portfolio. That sounds dramatic until you sit inside a portfolio review and realise half the "defensible software assets" are really just workflow toll booths wearing Patagonia vests.',
      },
      {
        type: 'paragraph',
        text: 'The winners will pull away quickly. AI-native products, data advantage, pricing power, better margins. The rest will discover that "mission-critical" is not the same as "irreplaceable."',
      },
      {
        type: 'paragraph',
        text: 'Anyone who bought vertical SaaS between 2020 and 2022 should be staring very hard at what they actually own.',
      },
      {
        type: 'paragraph',
        text: 'Vertical SaaS was meant to be the safest room in PE. It may just be the one without a fire exit.',
      },
    ],
  },
};

export { blogPosts };

// ---------------------------------------------------------------------------
// BlogPostPage Component
// ---------------------------------------------------------------------------

// Helpers --------------------------------------------------------------
// Derive a short, SEO-friendly description from the first paragraph of
// the post. Sliced to ~155 chars (Google's typical truncation point)
// with a clean word boundary so the snippet doesn't end mid-token.
function buildPostDescription(post) {
  const firstParagraph = post.content?.find((block) => block.type === 'paragraph');
  if (!firstParagraph?.text) return '';
  const text = firstParagraph.text.replace(/\s+/g, ' ').trim();
  if (text.length <= 155) return text;
  const sliced = text.slice(0, 155);
  const lastSpace = sliced.lastIndexOf(' ');
  return `${sliced.slice(0, lastSpace > 100 ? lastSpace : 155).trim()}…`;
}

// Loose mapping from human "March 2026" / "Q4 2024 Analysis" to ISO so
// BlogPosting schema gets a valid datePublished. Falls back to today.
// Exported so the index can sort newest-first off the same parse rather than
// keeping a second, drifting copy of the month table.
export function publishedDateISO(post) {
  if (!post?.date) return new Date().toISOString();
  const monthMap = {
    January: '01', February: '02', March: '03', April: '04', May: '05', June: '06',
    July: '07', August: '08', September: '09', October: '10', November: '11', December: '12',
  };
  const m = post.date.match(/(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{4})/);
  if (m) return `${m[2]}-${monthMap[m[1]]}-01`;
  return new Date().toISOString();
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const theme = useTheme();
  const ink = theme.palette.text.primary;
  const post = blogPosts[slug];

  if (!post) {
    return <Navigate to="/" replace />;
  }

  // ── Per-post SEO ─────────────────────────────────────────────────
  const description = buildPostDescription(post);
  const ogImage = post.image || `/blog/${slug}.jpg`;
  const published = publishedDateISO(post);
  // BlogPosting rather than the generic Article: these are dated posts in a
  // blog, and the more specific type is what Google expects for /blog/*.
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description,
    image: `https://re-stack.com${ogImage}`,
    datePublished: published,
    dateModified: published,
    author: {
      '@type': 'Organization',
      name: 'Restack',
      url: 'https://re-stack.com/',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Restack',
      logo: {
        '@type': 'ImageObject',
        url: 'https://re-stack.com/logo.svg',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://re-stack.com/blog/${slug}`,
    },
    articleSection: post.category,
    inLanguage: 'en',
  };

  // Next post to read, so the article ends on a route forward instead of a
  // dead end. Wraps around, and is skipped when this is the only post.
  const slugs = Object.keys(blogPosts);
  const nextSlug = slugs.length > 1
    ? slugs[(slugs.indexOf(slug) + 1) % slugs.length]
    : null;
  const nextPost = nextSlug ? blogPosts[nextSlug] : null;

  return (
    <LandingContainer
      data-theme="light"
      sx={{ position: 'relative' }}
    >
      <SEO
        title={post.title}
        description={description}
        canonical={`/blog/${slug}`}
        ogImage={ogImage}
        ogImageAlt={post.title}
        ogType="article"
        articleMeta={{
          publishedTime: published,
          section: post.category,
        }}
        schema={articleSchema}
      />
      {/* Shared marketing header - matches the landing page. */}
      <SiteHeader />

      {/* maxWidth is the reading measure, not a layout preference. With the
          body size set below, 680 (632 inside the container padding) lands
          around 73 characters a line; the previous 720 at the shared body size
          ran to roughly 100, which is well past comfortable for long-form. */}
      <Container
        component="main"
        id="main-content"
        sx={{
          position: 'relative',
          zIndex: 1,
          maxWidth: 680,
          pt: { xs: 'calc(56px + 1.5rem)', md: 'calc(64px + 2.5rem)' },
          pb: { xs: 6, md: 10 },
        }}
      >
        {/* BlogArticleBody is shared with the post lightbox, where the tighter
            type is right. Sizing up from the caller keeps the standalone page
            readable without changing how the modal renders. The descendant
            selector outranks the child's own sx by specificity, so this is
            deterministic rather than a load-order gamble. */}
        <Box
          component="article"
          sx={{
            '& p': {
              fontSize: { xs: '1rem', md: '1.0625rem' },
              lineHeight: 1.75,
              textWrap: 'pretty',
            },
            '& p:first-of-type': {
              fontSize: { xs: '1.05rem', md: '1.125rem' },
              lineHeight: 1.7,
            },
            '& h2': { fontSize: { xs: '1.2rem', md: '1.35rem' } },
          }}
        >
          <BlogArticleBody post={post} />
        </Box>

        {/* Post footer: byline, then a route forward. An article that ends on
            nothing but a back link is a dead end. */}
        <Box
          component="footer"
          sx={{ mt: { xs: 6, md: 8 }, pt: 4, borderTop: `1px solid ${alpha(ink, 0.08)}` }}
        >
          <Typography
            sx={{
              fontFamily: BRAND_SANS_FONT,
              fontSize: '0.82rem',
              color: alpha(ink, 0.55),
              mb: { xs: 4, md: 5 },
            }}
          >
            Written by the Restack team. Published{' '}
            <Box component="time" dateTime={published}>{post.date}</Box>.
          </Typography>

          {/* CTA back into the product - same primary wording as the rest of
              the site, so the ask is one thing everywhere. */}
          <Box
            sx={{
              p: { xs: 3, md: 3.5 },
              borderRadius: '20px',
              border: `1px solid ${alpha(ink, 0.09)}`,
              background: `linear-gradient(135deg, ${alpha(ACCENT, 0.05)} 0%, ${alpha('#FAF9F7', 0.6)} 100%)`,
            }}
          >
            <Typography
              component="h2"
              sx={{
                fontFamily: BRAND_SANS_FONT,
                fontSize: { xs: '1.05rem', md: '1.15rem' },
                fontWeight: 600,
                letterSpacing: '-0.015em',
                lineHeight: 1.35,
                color: 'text.primary',
                mb: 1,
              }}
            >
              See this run on your own deck.
            </Typography>
            <Typography
              sx={{
                fontFamily: BRAND_SANS_FONT,
                fontSize: '0.9rem',
                lineHeight: 1.6,
                color: alpha(ink, 0.7),
                mb: 2.5,
                maxWidth: 460,
              }}
            >
              Fifteen minutes, your template, a real slide rebuilt live inside
              PowerPoint.
            </Typography>
            <Button
              component={RouterLink}
              to="/demo"
              endIcon={<ArrowForwardRounded sx={{ fontSize: '1rem' }} />}
              sx={{
                textTransform: 'none',
                fontFamily: BRAND_SANS_FONT,
                fontWeight: 600,
                fontSize: '0.88rem',
                borderRadius: '999px',
                px: 2.75,
                py: 1,
                color: '#FFFFFF',
                background: 'linear-gradient(135deg, #B83B1D 0%, #D4694E 100%)',
                boxShadow: '0 4px 14px rgba(184, 59, 29, 0.22)',
                ...btnStates,
                '&:hover': {
                  background: 'linear-gradient(135deg, #A63419 0%, #C25A42 100%)',
                  boxShadow: '0 6px 18px rgba(184, 59, 29, 0.3)',
                },
              }}
            >
              See it on your own deck
            </Button>
          </Box>

          {/* Read next + back to the index. The back link pointed at the home
              page's blog strip (/#blog); the index itself is the right target
              and a router link keeps it from reloading the whole app. */}
          <Box
            sx={{
              mt: { xs: 3.5, md: 4 },
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            {nextPost && (
              <Box>
                <Typography
                  sx={{
                    fontFamily: BRAND_SANS_FONT,
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: alpha(ink, 0.45),
                    mb: 0.75,
                  }}
                >
                  Read next
                </Typography>
                <Link
                  component={RouterLink}
                  to={`/blog/${nextSlug}`}
                  underline="none"
                  sx={{
                    fontFamily: BRAND_SANS_FONT,
                    fontSize: '0.98rem',
                    fontWeight: 600,
                    lineHeight: 1.35,
                    letterSpacing: '-0.01em',
                    color: 'text.primary',
                    borderRadius: '6px',
                    transition: 'color 0.2s ease',
                    '&:hover': { color: ACCENT },
                    '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: '3px' },
                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                  }}
                >
                  {nextPost.title}
                </Link>
              </Box>
            )}
            {/* /news, not /blog. Both render this index, but only /news has a
                prerender-routes entry, so /blog's static head still carries the
                home page's canonical. Link the canonical URL. */}
            <Button
              component={RouterLink}
              to="/news"
              variant="text"
              startIcon={<ArrowBackIcon />}
              sx={{
                fontFamily: BRAND_SANS_FONT,
                fontWeight: 500,
                textTransform: 'none',
                color: ACCENT,
                ...btnStates,
                '&:hover': { backgroundColor: alpha(ACCENT, 0.06) },
              }}
            >
              Back to all articles
            </Button>
          </Box>
        </Box>
      </Container>

      <Footer />
    </LandingContainer>
  );
}
